/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Mixpanel utilities
 *
 * @file MixpanelUtils.js
 * @author Mohamed yasar arabath M
 */

import CommonUtils from './CommonUtils';

export default class MixpanelUtils {
  static isRecording = false;

  static recordTimer = null;

  static timeoutMS = 6000 * 10; // 10 minutes

  static replayId = null;

  /**
   * Mixpanel start session recording
   *
   * @param {*} enableMask
   */
  static startSessionRecording(enableMask = false, enableAutoRecord = true) {
    if (window.mixpanel) {
      this.replayId = enableAutoRecord && window.mixpanel.get_session_recording_properties()?.$mp_replay_id;
      const overRideConfig = enableMask
        ? { record_mask_text_selector: '*', record_block_selector: 'img, video' }
        : { record_mask_text_selector: '', record_block_selector: null };
      if (enableAutoRecord) {
        overRideConfig.record_sessions_percent = 100;
        overRideConfig.record_idle_timeout_ms = this.timeoutMS;
      }
      this.overRideConfig(overRideConfig);
      if (!this.replayId) {
        window.mixpanel.start_session_recording();
        setTimeout(() => {
          this.replayId = window.mixpanel.get_session_recording_properties()?.$mp_replay_id;
        }, 2000);
      }
    }
  }

  /**
   * start mixpanel Session Recorder with timer
   *
   * @param {*} timeOutMS
   * @returns
   */
  static async startSessionRecordTimer(timeOutMS = this.timeoutMS) {
    this.checkMixpanelInstance(() => {
      this.startSessionRecording();
    });
    return new Promise((resolve) => {
      this.recordTimer = setInterval(() => {
        const currentReplayId = window.mixpanel.get_session_recording_properties()?.$mp_replay_id;
        if (!currentReplayId || (currentReplayId && currentReplayId !== MixpanelUtils.replayId)) {
          this.stopSessionRecordTimer();
          resolve();
        }
      }, timeOutMS);
    });
  }

  /**
   *  stop Session Recorder Timer
   *
   */
  static stopSessionRecordTimer() {
    clearInterval(this.recordTimer);
    this.endSessionRecording();
  }

  /**
   * Mixpanel end session recording
   *
   */
  static endSessionRecording() {
    const replayId = window?.mixpanel?.get_session_recording_properties()?.$mp_replay_id;

    if (window?.mixpanel && replayId) {
      window.mixpanel.stop_session_recording();
    }
  }

  /**
   * Mixpanel config overRide
   *
   * @param {*} overRideConfig
   */
  static overRideConfig(overRideConfig) {
    if (window.mixpanel && !CommonUtils.isEmpty(overRideConfig)) {
      window.mixpanel.set_config({ ...overRideConfig });
    }
  }

  /**
   * Check if mixpanel instance is available
   * @param {*} callback - callback function to be executed when mixpanel instance is available
   * @param {*} attempts - number of attempts to check if mixpanel instance is available
   * @param {*} maxAttempts - maximum number of attempts to check if mixpanel instance is available
   */
  static checkMixpanelInstance(callback = () => {}, attempts = 0, maxAttempts = 100) {
    try {
      const id = window.mixpanel.get_distinct_id();
      if (id) {
        callback();
      }
    } catch (e) {
      if (attempts < maxAttempts) {
        setTimeout(() => this.checkMixpanelInstance(callback, attempts + 1), 100);
      }
    }
  }
}
